import React from "react";
import { ShieldCheck, Mail } from "lucide-react";
import { useI18n } from "../../i18n";
import { Container, Section } from "../../components/ui/Primitives";
import { PageHero } from "../../components/marketing/Shared";
import { CONTACT_EMAIL, CONTACT_MAILTO } from "../../lib/contact";

// Última atualização (data fixa; ajuste ao publicar uma nova versão).
const UPDATED = "21 de julho de 2026";

// Estrutura da política. As seções são numeradas automaticamente pela ordem do
// array. Cada bloco é { p } (parágrafo), { ul } (lista), { h } (subtítulo),
// { dl } (glossário) ou { contact } (canal do Encarregado).
// Documento em PT-BR (empresa brasileira; LGPD + GDPR).
const SECTIONS = [
  {
    id: "introducao",
    title: "Introdução",
    blocks: [
      { p: "Esta Política de Privacidade descreve como o SecFace — serviço de verificação de prova de vida (liveness) e antifraude facial, um produto da sec4us (“SecFace”, “nós”) — coleta, utiliza, compartilha e protege dados pessoais, em conformidade com a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais — LGPD) e com o Regulamento (UE) 2016/679 (General Data Protection Regulation — GDPR), quando aplicável." },
      { p: "O SecFace é um serviço embarcado por outros sistemas (nossos clientes) por meio de um fluxo de redirecionamento no estilo OAuth: um sistema cliente inicia uma verificação, o usuário é redirecionado para uma página hospedada onde a prova de vida é realizada (rosto, movimento, flash e gesto) e o resultado é devolvido ao sistema cliente por um canal autenticado." },
      { p: "Ao utilizar o SecFace ou passar por uma verificação hospedada, você declara estar ciente das práticas aqui descritas. Recomendamos a leitura integral deste documento." },
    ],
  },
  {
    id: "definicoes",
    title: "Definições",
    blocks: [
      { p: "Para facilitar a leitura, adotamos os seguintes conceitos:" },
      {
        dl: [
          ["Dados pessoais", "informação relacionada a pessoa natural identificada ou identificável."],
          ["Dados pessoais sensíveis", "categoria especial que inclui dados biométricos vinculados a uma pessoa natural, sujeita a proteção reforçada."],
          ["Titular", "a pessoa natural a quem se referem os dados pessoais."],
          ["Controlador", "quem toma as decisões sobre o tratamento dos dados pessoais."],
          ["Operador", "quem realiza o tratamento em nome e sob as instruções do controlador."],
          ["Encarregado (DPO)", "pessoa indicada para atuar como canal de comunicação entre o titular, o controlador e a autoridade de proteção de dados."],
          ["Anonimização", "processo que faz um dado perder a possibilidade de associação a um indivíduo; o dado anonimizado não é considerado dado pessoal."],
          ["Liveness (prova de vida)", "verificação de que há uma pessoa real e presente diante da câmera."],
          ["PAD (antifraude)", "detecção de ataques de apresentação (impressões, telas, máscaras, deepfakes e replays)."],
          ["ANPD", "Autoridade Nacional de Proteção de Dados, no Brasil."],
        ],
      },
    ],
  },
  {
    id: "papel",
    title: "A quem se aplica e nosso papel",
    blocks: [
      { p: "Esta política aplica-se a titulares que passam por uma verificação hospedada pelo SecFace, a visitantes do nosso site e a representantes de clientes e potenciais clientes. Dependendo do contexto, atuamos em papéis distintos definidos pela LGPD (art. 5º, VI e VII) e pelo GDPR (art. 4(7) e 4(8)):" },
      {
        ul: [
          "Operador (processor): nas verificações de liveness solicitadas por um cliente. O cliente (controlador) decide por que a verificação ocorre e quais checagens são habilitadas; nós tratamos os dados em nome e sob as instruções desse cliente.",
          "Controlador (controller): quando tratamos dados por conta própria — por exemplo, dados de visitantes do site, contatos comerciais e solicitações de demonstração, comunicações e recrutamento.",
          "Controlador independente: para finalidades legítimas de segurança, prevenção e detecção de fraude e melhoria dos nossos modelos (nesta última, apenas com dados anonimizados ou pseudonimizados), sempre nos limites da lei aplicável.",
        ],
      },
      { p: "Quando atuamos como operador, o controlador é o sistema cliente que iniciou a verificação. Nesses casos, o exercício de direitos sobre esses dados deve, em regra, ser direcionado a esse controlador (veja a seção “Como exercer seus direitos”)." },
    ],
  },
  {
    id: "dados",
    title: "Dados pessoais que tratamos",
    blocks: [
      { p: "Podemos tratar as seguintes categorias de dados. Nem toda verificação envolve todas elas. Os dados nos chegam do sistema cliente que inicia a verificação e da própria captura realizada por você na página hospedada." },
      { h: "Dados de identificação e contato" },
      { ul: ["Nome e endereço de e-mail, fornecidos pelo sistema cliente ao iniciar a verificação (ou por você, ao solicitar uma demonstração)."] },
      { h: "Dados biométricos e de prova de vida (dados pessoais sensíveis)" },
      {
        ul: [
          "Imagens e vídeo do rosto capturados durante a verificação, incluindo o recorte do rosto, o quadro completo e o quadro do gesto.",
          "Representações matemáticas do rosto (embeddings faciais) utilizadas para comparação e antifraude.",
          "Sinais e escores de prova de vida e antifraude (PAD): movimento/profundidade, reflexo de flash (Flashmark), gesto solicitado e indicadores anti-spoofing.",
          "Quando fornecida pelo sistema cliente, uma foto de referência ou o recorte de foto de documento, para comparação facial.",
        ],
      },
      { h: "Dados técnicos, de dispositivo e localização" },
      { ul: ["Endereço IP, tipo de navegador e sistema operacional, identificadores de sessão, carimbos de tempo, telemetria de segurança e localização aproximada (derivada do dispositivo, quando autorizada, ou do IP)."] },
      { h: "Metadados da sessão" },
      { ul: ["Identificação do sistema cliente, URLs de redirecionamento, estado (state), idioma e demais metadados técnicos necessários ao fluxo de verificação."] },
    ],
  },
  {
    id: "finalidades",
    title: "Finalidades do tratamento",
    blocks: [
      { p: "Tratamos dados pessoais para as seguintes finalidades:" },
      {
        ul: [
          "Realizar a verificação de prova de vida (liveness) e a análise antifraude (PAD), confirmando que se trata de uma pessoa real e presente.",
          "Realizar, quando solicitado, a comparação facial contra uma foto de referência ou documento.",
          "Prevenir, detectar e mitigar fraudes, incluindo tentativas de spoofing, deepfake e injeção de câmera.",
          "Entregar o resultado da verificação ao sistema cliente que a solicitou, por um canal autenticado.",
          "Garantir a segurança, a integridade e a disponibilidade do serviço, bem como manter trilhas de auditoria.",
          "Cumprir obrigações legais e regulatórias e exercer regularmente direitos em processos.",
          "Aprimorar a acurácia dos nossos modelos de liveness e antifraude, exclusivamente com dados anonimizados ou pseudonimizados (veja a seção sobre melhoria de modelos).",
        ],
      },
    ],
  },
  {
    id: "bases",
    title: "Bases legais",
    blocks: [
      { p: "O tratamento se apoia nas seguintes bases legais, conforme a finalidade e a legislação aplicável." },
      { h: "LGPD (Lei nº 13.709/2018)" },
      {
        ul: [
          "Para dados pessoais em geral: execução de contrato e procedimentos preliminares (art. 7º, V); cumprimento de obrigação legal ou regulatória (art. 7º, II); legítimo interesse, notadamente a prevenção à fraude e a segurança (art. 7º, IX); e exercício regular de direitos (art. 7º, VI).",
          "Para dados pessoais sensíveis (biométricos): consentimento específico e destacado do titular (art. 11, I); e/ou a garantia da prevenção à fraude e à segurança do titular nos processos de identificação e autenticação de cadastro em sistemas eletrônicos (art. 11, II, “g”), resguardados os direitos do titular.",
        ],
      },
      { h: "GDPR (Regulamento (UE) 2016/679)" },
      {
        ul: [
          "Para dados pessoais em geral: execução de contrato (art. 6(1)(b)); obrigação legal (art. 6(1)(c)); e interesse legítimo, incluindo a prevenção à fraude (art. 6(1)(f)).",
          "Para categorias especiais de dados (biométricos tratados para identificar uma pessoa de forma única — art. 9): consentimento explícito (art. 9(2)(a)) e, quando aplicável, constituição, exercício ou defesa de direitos (art. 9(2)(f)) ou interesse público relevante (art. 9(2)(g)).",
        ],
      },
      { p: "Quando o tratamento se basear em consentimento, você poderá revogá-lo a qualquer momento, sem prejuízo da licitude do tratamento realizado antes da revogação." },
    ],
  },
  {
    id: "biometria",
    title: "Tratamento de dados biométricos",
    blocks: [
      { p: "Dados biométricos são dados pessoais sensíveis e recebem proteção reforçada. Utilizamos os dados biométricos estritamente para as finalidades de prova de vida, comparação facial e antifraude descritas nesta política, e não os utilizamos para publicidade, perfilização comportamental ou qualquer outra finalidade incompatível." },
      { p: "Não vendemos, alugamos nem comercializamos dados ou identificadores biométricos. O resultado biométrico nunca trafega no redirecionamento do navegador — apenas um código de uso único é transportado, e o resultado é obtido pelo sistema cliente por um canal servidor-a-servidor autenticado." },
      { p: "A retenção de dados biométricos observa, em todos os casos, os limites e prazos das leis de privacidade biométrica aplicáveis; quando a lei exigir prazo menor ou eliminação anterior, prevalece a regra mais restritiva." },
    ],
  },
  {
    id: "compartilhamento",
    title: "Compartilhamento de dados e suboperadores",
    blocks: [
      { p: "Podemos compartilhar dados pessoais com:" },
      {
        ul: [
          "O sistema cliente que solicitou a verificação, ao qual entregamos o resultado (e, quando configurado, as imagens capturadas), para as finalidades por ele definidas.",
          "Suboperadores e prestadores de serviço que atuam em nosso nome — por exemplo, infraestrutura e hospedagem em nuvem, armazenamento, envio de e-mail e serviços de apoio à análise antifraude — sujeitos a obrigações contratuais de confidencialidade e segurança.",
          "Autoridades públicas, reguladores e o Poder Judiciário, quando exigido por lei, ordem judicial ou solicitação legítima.",
          "Sucessores, em caso de reorganização societária, fusão, aquisição ou operação semelhante, resguardadas as proteções de confidencialidade.",
        ],
      },
      { p: "A lista atualizada de suboperadores pode ser disponibilizada a clientes mediante solicitação ao nosso Encarregado. Não vendemos dados pessoais." },
    ],
  },
  {
    id: "transferencia",
    title: "Transferência internacional de dados",
    blocks: [
      { p: "Poderemos tratar e armazenar dados em servidores localizados fora do país do titular. Quando isso implicar transferência internacional, adotamos salvaguardas reconhecidas pela legislação aplicável, tais como decisões de adequação, cláusulas contratuais padrão (Standard Contractual Clauses da Comissão Europeia e adendos equivalentes) e as cláusulas-padrão e demais mecanismos previstos pela LGPD (art. 33 e seguintes) e pela ANPD." },
    ],
  },
  {
    id: "retencao",
    title: "Retenção e eliminação",
    blocks: [
      { p: "Mantemos os dados pessoais apenas pelo tempo necessário ao cumprimento das finalidades para as quais foram coletados, ao atendimento de obrigações legais e regulatórias e ao exercício regular de direitos." },
      { p: "Nas verificações em que atuamos como operador, o período de retenção é definido pelo controlador (sistema cliente). Sempre que possível, minimizamos a retenção de imagens brutas e de dados biométricos, eliminando-os ou anonimizando-os quando não mais necessários. A pedido do controlador, eliminamos os dados da sessão." },
      { p: "Findos os prazos aplicáveis, os dados são eliminados ou anonimizados de forma segura, ressalvadas as hipóteses de guarda autorizadas pela lei." },
    ],
  },
  {
    id: "seguranca",
    title: "Segurança da informação",
    blocks: [
      { p: "Adotamos medidas técnicas e organizacionais para proteger os dados pessoais, incluindo:" },
      {
        ul: [
          "Criptografia em trânsito (TLS) e em repouso (AES).",
          "Isolamento por cliente (multi-tenant) e controle de acesso com privilégio mínimo.",
          "Código de uso único e troca de resultado servidor-a-servidor, evitando que o veredito trafegue no navegador.",
          "Vereditos terminais imutáveis (uma sessão finalizada não pode ser revertida) e trilhas de auditoria.",
          "Assinatura de webhooks por HMAC, listas de IP permitidas e limites de requisição por token.",
        ],
      },
      { p: "Nenhuma medida de segurança é infalível. Você também é responsável por proteger seus dispositivos, credenciais e comunicações." },
    ],
  },
  {
    id: "decisoes-automatizadas",
    title: "Decisões automatizadas",
    blocks: [
      { p: "A verificação de liveness produz um resultado automatizado (aprovado/reprovado) a partir da análise das capturas. A decisão final sobre conceder ou não acesso, aprovar um cadastro ou autorizar uma operação é tomada pelo sistema cliente, e não pelo SecFace." },
      { p: "Nos termos do art. 20 da LGPD e do art. 22 do GDPR, o titular pode solicitar informações sobre os critérios utilizados e a revisão de decisões tomadas unicamente com base em tratamento automatizado que o afetem. Solicitações relativas a uma verificação específica devem ser direcionadas ao sistema cliente (controlador) e, subsidiariamente, a nós." },
    ],
  },
  {
    id: "direitos",
    title: "Direitos do titular",
    blocks: [
      { p: "Você pode exercer, a qualquer tempo, os direitos assegurados pela LGPD (art. 18) e pelo GDPR (art. 15 a 22), incluindo:" },
      {
        ul: [
          "Confirmação da existência de tratamento e acesso aos dados.",
          "Correção de dados incompletos, inexatos ou desatualizados.",
          "Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou tratados em desconformidade.",
          "Portabilidade dos dados a outro fornecedor, nos termos da regulamentação.",
          "Informação sobre o compartilhamento de dados e sobre a possibilidade de não fornecer consentimento.",
          "Revogação do consentimento e oposição a tratamento fundado em interesse legítimo.",
          "Revisão de decisões automatizadas e apresentação de reclamação à autoridade de controle.",
        ],
      },
    ],
  },
  {
    id: "deveres",
    title: "Deveres do titular",
    blocks: [
      { p: "Para que a verificação e a proteção dos seus dados funcionem adequadamente, você se compromete a:" },
      {
        ul: [
          "Fornecer informações verdadeiras, exatas e atualizadas durante a verificação.",
          "Não utilizar o serviço para finalidades ilícitas nem tentar se passar por outra pessoa ou burlar os mecanismos de prova de vida e antifraude.",
          "Manter a confidencialidade de eventuais códigos e credenciais recebidos e não os repassar a terceiros.",
        ],
      },
    ],
  },
  {
    id: "como-exercer",
    title: "Como exercer seus direitos e Encarregado (DPO)",
    blocks: [
      { p: "Quando atuamos como controlador, você pode exercer seus direitos entrando em contato com o nosso Encarregado pela Proteção de Dados (Data Protection Officer)." },
      { p: "Quando atuamos como operador (verificações solicitadas por um cliente), encaminhe sua solicitação ao sistema cliente que iniciou a verificação; podemos auxiliar o controlador no atendimento." },
      { contact: true },
      { p: "A razão social, o CNPJ e o endereço completos do controlador podem ser obtidos junto ao Encarregado. Responderemos às solicitações nos prazos previstos na legislação aplicável." },
    ],
  },
  {
    id: "cookies",
    title: "Cookies",
    blocks: [
      { p: "Nosso site utiliza cookies para operar as páginas e melhorar a experiência de navegação. Usuários submetidos a uma verificação recebem, na página hospedada, aviso claro de consentimento e de gravação. Você pode gerenciar preferências de cookies no seu navegador e por meio do aviso exibido no site." },
    ],
  },
  {
    id: "criancas",
    title: "Crianças e adolescentes",
    blocks: [
      { p: "O SecFace não é direcionado a crianças. Quando um sistema cliente utiliza a verificação para públicos que possam incluir menores, o tratamento observa a base legal adequada e o melhor interesse do menor, sob responsabilidade do respectivo controlador, em conformidade com a LGPD e, no Brasil, com o Estatuto Digital da Criança e do Adolescente (Lei nº 15.211/2025 — “ECA Digital”)." },
    ],
  },
  {
    id: "modelos",
    title: "Melhoria de modelos e prevenção à fraude",
    blocks: [
      { p: "Podemos utilizar dados anonimizados ou pseudonimizados para treinar, avaliar e aprimorar nossos modelos de liveness e antifraude e para reforçar a detecção de fraude entre clientes. Dados anonimizados, por não permitirem a identificação do titular, deixam de ser considerados dados pessoais." },
      { p: "O controlador pode solicitar a exclusão de registros de verificação, o que impede o uso prospectivo desses dados para tais finalidades." },
    ],
  },
  {
    id: "alteracoes",
    title: "Alterações desta Política",
    blocks: [
      { p: "Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças legais, técnicas ou de negócio. A versão vigente é sempre a publicada nesta página, identificada pela data de última atualização. Alterações relevantes poderão ser comunicadas pelos canais adequados." },
    ],
  },
  {
    id: "contato",
    title: "Contato e autoridade de controle",
    blocks: [
      { p: "Em caso de dúvidas sobre esta política ou sobre o tratamento dos seus dados, fale com o nosso Encarregado pelos canais indicados acima." },
      { p: "Você também pode apresentar reclamação à Autoridade Nacional de Proteção de Dados (ANPD), no Brasil, ou à autoridade de controle competente no seu país, no âmbito do GDPR." },
    ],
  },
];

function Blocks({ blocks }) {
  return blocks.map((b, i) => {
    if (b.h) return <h3 key={i} className="mt-6 text-base font-bold text-foreground">{b.h}</h3>;
    if (b.dl)
      return (
        <dl key={i} className="mt-3 space-y-2.5">
          {b.dl.map(([term, def], j) => (
            <div key={j} className="text-sm leading-relaxed">
              <dt className="inline font-semibold text-foreground">{term}: </dt>
              <dd className="inline text-muted-foreground">{def}</dd>
            </div>
          ))}
        </dl>
      );
    if (b.ul)
      return (
        <ul key={i} className="mt-3 space-y-2">
          {b.ul.map((it, j) => (
            <li key={j} className="flex gap-2.5 text-sm leading-relaxed text-muted-foreground">
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    if (b.contact)
      return (
        <a
          key={i}
          href={CONTACT_MAILTO}
          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-4 py-2.5 text-sm font-semibold text-brand hover:bg-accent"
        >
          <Mail className="h-4 w-4" /> {CONTACT_EMAIL}
        </a>
      );
    return <p key={i} className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.p}</p>;
  });
}

export default function Privacy() {
  const { t } = useI18n();
  return (
    <>
      <PageHero
        breadcrumb={t("footer.privacy")}
        eyebrow={t("footer.privacy")}
        title="Política de Privacidade"
        lead="Como o SecFace coleta, utiliza, compartilha e protege dados pessoais — incluindo dados biométricos — em conformidade com a LGPD e o GDPR."
      >
        <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 font-mono text-xs text-white/80">
          <ShieldCheck className="h-3.5 w-3.5" /> Última atualização: {UPDATED}
        </span>
      </PageHero>

      <Section>
        <Container>
          <div className="grid gap-10 lg:grid-cols-[220px_1fr]">
            {/* Índice */}
            <nav className="hidden lg:block">
              <div className="sticky top-24">
                <p className="mb-3 font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground">Índice</p>
                <ul className="space-y-1.5">
                  {SECTIONS.map((s, i) => (
                    <li key={s.id}>
                      <a href={`#${s.id}`} className="block text-sm text-muted-foreground transition-colors hover:text-brand">
                        {i + 1}. {s.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </nav>

            {/* Conteúdo */}
            <div className="max-w-3xl">
              {SECTIONS.map((s, i) => (
                <section key={s.id} id={s.id} className="scroll-mt-24 border-b border-border pb-8 pt-8 first:pt-0 last:border-0">
                  <h2 className="text-xl font-bold text-foreground sm:text-2xl">{i + 1}. {s.title}</h2>
                  <Blocks blocks={s.blocks} />
                </section>
              ))}
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}
